/**
 * FlowAI Playbook UI-Builder (#1173) — Form-basierter Editor für DSL-Steps.
 *
 * @page /flowai/playbooks/:id/builder
 * @since v2.514.0
 *
 * Parallel zum Markdown-Editor. Draft-Modus via flowai_playbooks.steps_draft.
 * Publish bumped Version + INSERT in flowai_playbook_versions.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, Card, Button, Badge } from '../../components/ui';
import FlowAIBreadcrumb from '../../components/flowai/FlowAIBreadcrumb';
import StepList from '../../components/flowai/StepList';
import StepDetailPanel from '../../components/flowai/StepDetailPanel';
import TestRunModal from '../../components/flowai/TestRunModal';

const API = import.meta.env.VITE_API_URL || '';

function newStepTemplate(idx) {
  return {
    id: `step_${idx + 1}`,
    type: 'builtin',
    action: 'noop',
    inputs: {},
  };
}

export default function FlowAIPlaybookBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [playbook, setPlaybook] = useState(null);
  const [catalog, setCatalog] = useState({
    skill_library: { prompt_template: [], tool_call: [] },
    variable_scopes: [],
    builtins: [],
    action_types: [],
    operators: [],
  });
  const [steps, setSteps] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [validation, setValidation] = useState({ valid: true, errors: [], cross_step_refs: [] });
  const [stepStatuses, setStepStatuses] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [validatingNow, setValidatingNow] = useState(false);
  const [testStepOpen, setTestStepOpen] = useState(false);
  const [testPipelineOpen, setTestPipelineOpen] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  async function load() {
    setLoadErr(null);
    try {
      const r = await fetch(`${API}/api/flowai/playbooks/${id}/builder`, { credentials: 'include' });
      if (!r.ok) { setLoadErr(`HTTP ${r.status}`); return; }
      const d = await r.json();
      setPlaybook(d.playbook);
      setCatalog({
        skill_library: d.skill_library || { prompt_template: [], tool_call: [] },
        variable_scopes: d.variable_scopes || [],
        builtins: d.builtins || [],
        action_types: d.action_types || [],
        operators: d.operators || [],
      });
      const draft = d.playbook.steps_draft;
      const live = d.playbook.steps;
      const initial = Array.isArray(draft) && draft.length ? draft : (Array.isArray(live) ? live : []);
      setSteps(initial);
      setSelectedId(initial[0]?.id || null);
      setDirty(Array.isArray(draft) && draft.length > 0);
    } catch (e) {
      setLoadErr(String(e));
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Phase 4: Live-Validation — debounced (800ms) auf jeden steps-Edit.
  useEffect(() => {
    if (!playbook || steps.length === 0) return;
    const t = setTimeout(() => { validateNow(); }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [steps]);

  const selectedIdx = useMemo(() => steps.findIndex((s) => s.id === selectedId), [steps, selectedId]);
  const selectedStep = selectedIdx >= 0 ? steps[selectedIdx] : null;
  const priorSteps = selectedIdx > 0 ? steps.slice(0, selectedIdx) : [];

  function mutate(nextSteps) {
    setSteps(nextSteps);
    setDirty(true);
  }

  function updateSelectedStep(updated) {
    const next = [...steps];
    const oldId = selectedStep?.id;
    next[selectedIdx] = updated;
    mutate(next);
    if (updated.id !== oldId) setSelectedId(updated.id);
  }

  function addStep() {
    const next = [...steps, newStepTemplate(steps.length)];
    mutate(next);
    setSelectedId(next[next.length - 1].id);
  }

  function deleteStep(idx) {
    const next = steps.filter((_, i) => i !== idx);
    mutate(next);
    if (selectedIdx === idx) setSelectedId(next[Math.max(0, idx - 1)]?.id || null);
  }

  function duplicateStep(idx) {
    const cur = steps[idx];
    const clone = { ...cur, id: `${cur.id || 'step'}_copy` };
    const next = [...steps.slice(0, idx + 1), clone, ...steps.slice(idx + 1)];
    mutate(next);
    setSelectedId(clone.id);
  }

  async function saveDraft() {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/flowai/playbooks/${id}/draft`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ steps_draft: steps }),
      });
      if (r.ok) {
        setDirty(false);
      } else {
        const d = await r.json().catch(() => ({}));
        alert('Draft-Save fehlgeschlagen: ' + (d.error || r.status));
      }
    } finally {
      setSaving(false);
    }
  }

  async function discardDraft() {
    if (!confirm('Draft verwerfen? Alle nicht-publizierten Änderungen gehen verloren.')) return;
    await fetch(`${API}/api/flowai/playbooks/${id}/draft`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ steps_draft: null }),
    });
    await load();
  }

  async function validateNow() {
    setValidatingNow(true);
    try {
      const r = await fetch(`${API}/api/flowai/playbooks/${id}/validate-draft`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ steps }),
      });
      const d = await r.json();
      setValidation(d);
      const next = {};
      for (const s of steps) next[s.id] = 'ok';
      for (const err of d.errors || []) {
        const m = err.match(/step '([^']+)'/);
        if (m) next[m[1]] = 'error';
      }
      setStepStatuses(next);
    } finally {
      setValidatingNow(false);
    }
  }

  async function publish() {
    if (!validation.valid) {
      if (!confirm('Validation hat Fehler. Trotzdem publishen versuchen?')) return;
    }
    const note = prompt('Optionale Change-Note für die neue Version:', 'UI-Builder Publish') || '';
    setPublishing(true);
    try {
      if (dirty) await saveDraft();
      const r = await fetch(`${API}/api/flowai/playbooks/${id}/publish`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ change_note: note }),
      });
      const d = await r.json();
      if (r.ok) {
        alert(`Publiziert als v${d.version_number}`);
        await load();
      } else {
        alert('Publish-Fehler: ' + (d.error || r.status) + (d.validation_errors ? '\n\n' + d.validation_errors.join('\n') : ''));
      }
    } finally {
      setPublishing(false);
    }
  }

  function stepLevelErrors(stepId) {
    return (validation.errors || []).filter((e) => e.includes(`'${stepId}'`));
  }

  if (loadErr) return <div className="p-6 text-sm text-rose-600">Lade-Fehler: {loadErr}</div>;
  if (!playbook) return <div className="p-6 text-sm">Lade…</div>;

  return (
    <div className="p-6">
      <FlowAIBreadcrumb trail={[
        { label: 'Playbooks', to: '/flowai/playbooks' },
        { label: playbook.name, to: `/flowai/playbooks/${id}` },
        { label: 'UI-Builder' },
      ]} />
      <PageHeader
        title={`${playbook.name} — UI-Builder`}
        subtitle={`Playbook #${playbook.id}${playbook.version_number ? ` · aktiv v${playbook.version_number}` : ''}${dirty ? ' · Draft (ungespeichert)' : ''}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" onClick={() => navigate(`/flowai/playbooks/${id}`)}>← Markdown-Editor</Button>
            <Button variant="ghost" onClick={() => setTestPipelineOpen(true)}>Pipeline testen</Button>
            <Button variant="ghost" onClick={discardDraft} disabled={!playbook.steps_draft && !dirty}>Draft verwerfen</Button>
            <Button variant="ghost" onClick={validateNow} disabled={validatingNow}>{validatingNow ? '…' : 'Validieren'}</Button>
            <Button onClick={saveDraft} disabled={saving || !dirty}>{saving ? 'Speichere…' : 'Draft speichern'}</Button>
            <Button onClick={publish} disabled={publishing}>{publishing ? 'Publizieren…' : 'Publizieren'}</Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
        <Badge variant={playbook.enabled ? 'success' : 'default'}>{playbook.enabled ? 'aktiv' : 'inaktiv'}</Badge>
        <Badge variant={playbook.use_dsl_engine ? 'info' : 'default'}>{playbook.use_dsl_engine ? 'Engine v2' : 'Engine v1'}</Badge>
        {playbook.workspace_name && <span className="text-[var(--text-tertiary)]">Workspace: <strong className="text-[var(--text-secondary)]">{playbook.workspace_name}</strong></span>}
        {Array.isArray(playbook.trigger_type) && playbook.trigger_type.length > 0 && (
          <span className="text-[var(--text-tertiary)]">Trigger: <code className="font-mono text-[var(--text-secondary)]">{playbook.trigger_type.join(', ')}</code></span>
        )}
        {playbook.draft_modified_at && (
          <span className="text-[var(--text-tertiary)]">Draft: {new Date(playbook.draft_modified_at).toLocaleString('de-DE')}{playbook.draft_modified_by_name ? ` von ${playbook.draft_modified_by_name}` : ''}</span>
        )}
      </div>

      {validation.errors?.length > 0 && (
        <Card className="mb-4 border-rose-200 bg-rose-50">
          <div className="text-xs font-semibold text-rose-700 mb-1">Validation-Fehler ({validation.errors.length})</div>
          <ul className="text-xs text-rose-700 list-disc list-inside space-y-0.5">
            {validation.errors.map((e, i) => <li key={i} className="font-mono">{e}</li>)}
          </ul>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-5">
          <Card title="Steps" subtitle={`${steps.length} Step${steps.length === 1 ? '' : 's'} · Drag&Drop zum Sortieren`}>
            <StepList
              steps={steps}
              selectedId={selectedId}
              statuses={stepStatuses}
              onSelect={setSelectedId}
              onReorder={mutate}
              onAdd={addStep}
              onDelete={deleteStep}
              onDuplicate={duplicateStep}
            />
          </Card>
        </div>
        <div className="lg:col-span-7">
          <Card title="Step-Details" subtitle={selectedStep ? `aktiv: ${selectedStep.id}` : 'Kein Step ausgewählt'}>
            <StepDetailPanel
              step={selectedStep}
              priorSteps={priorSteps}
              catalog={catalog}
              onChange={updateSelectedStep}
              onTestStep={() => setTestStepOpen(true)}
              validationErrors={selectedStep ? stepLevelErrors(selectedStep.id) : []}
            />
          </Card>
        </div>
      </div>

      <TestRunModal
        open={testStepOpen}
        onClose={() => setTestStepOpen(false)}
        playbookId={id}
        mode="step"
        stepId={selectedStep?.id}
      />
      <TestRunModal
        open={testPipelineOpen}
        onClose={() => setTestPipelineOpen(false)}
        playbookId={id}
        mode="pipeline"
        defaultEventType={Array.isArray(playbook.trigger_type) ? playbook.trigger_type[0] : 'case_stage_changed'}
      />
    </div>
  );
}
